import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Icons } from "../components/Icons.js";

/**
 * Sélection du micro et test d'enregistrement (Réglages → Capture). Diagnostic en libre-service :
 * l'utilisateur choisit une entrée audio, lance un court enregistrement en voyant le niveau en
 * direct, puis réécoute l'extrait. Si aucun son n'est détecté (niveau nul ou fichier minuscule), on
 * le prévient explicitement — la cause n°1 d'une transcription vide étant un micro muet ou mal choisi.
 *
 * Autonome : n'utilise pas la file de capture (aucun envoi réseau). Les noms des micros ne sont
 * lisibles qu'après une première autorisation, d'où le rafraîchissement des périphériques au test.
 */

type Phase = "idle" | "recording" | "done";

const TEST_MS = 4000;
// Seuils au-delà desquels on considère qu'aucun son n'a été capté (niveau crête et taille du fichier).
const SILENCE_PEAK = 0.02;
const MIN_BYTES = 2000;

function pickTestMime(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
  if (typeof MediaRecorder !== "undefined") {
    for (const m of candidates) if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

interface TestResult {
  url: string;
  size: number;
  seconds: number;
  peak: number;
}

export function MicTest({ deviceId, onDeviceChange }: { deviceId: string; onDeviceChange: (id: string) => void }) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [level, setLevel] = useState(0);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ref = useRef({
    stream: null as MediaStream | null,
    ctx: null as AudioContext | null,
    raf: 0,
    rec: null as MediaRecorder | null,
    chunks: [] as Blob[],
    peak: 0,
    url: null as string | null,
    timer: 0,
  });

  const refreshDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
    } catch {
      /* énumération indisponible : on garde la liste courante */
    }
  }, []);

  const teardown = useCallback(() => {
    const c = ref.current;
    if (c.raf) cancelAnimationFrame(c.raf);
    c.raf = 0;
    if (c.timer) window.clearTimeout(c.timer);
    c.timer = 0;
    try {
      c.rec?.stop();
    } catch {
      /* déjà arrêté */
    }
    c.rec = null;
    c.stream?.getTracks().forEach((t) => t.stop());
    c.stream = null;
    if (c.ctx) {
      void c.ctx.close().catch(() => undefined);
      c.ctx = null;
    }
    setLevel(0);
  }, []);

  useEffect(() => {
    void refreshDevices();
    return () => {
      teardown();
      const c = ref.current;
      if (c.url) URL.revokeObjectURL(c.url);
    };
  }, [refreshDevices, teardown]);

  const runTest = useCallback(async () => {
    const c = ref.current;
    setError(null);
    setResult(null);
    if (c.url) {
      URL.revokeObjectURL(c.url);
      c.url = null;
    }
    c.peak = 0;
    c.chunks = [];

    let stream: MediaStream;
    try {
      const audio: MediaTrackConstraints | boolean = deviceId ? { deviceId: { exact: deviceId } } : true;
      stream = await navigator.mediaDevices.getUserMedia({ audio });
    } catch (e) {
      setError(
        e instanceof DOMException
          ? "Micro indisponible ou autorisation refusée. Vérifiez les autorisations du navigateur."
          : "Démarrage du test impossible.",
      );
      return;
    }
    c.stream = stream;
    void refreshDevices(); // noms des micros désormais disponibles (autorisation accordée)

    // Niveau en direct (même mesure RMS que l'enregistrement réel).
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) {
      const ctx = new AC();
      c.ctx = ctx;
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) {
          const x = (v - 128) / 128;
          sum += x * x;
        }
        const lv = Math.min(1, Math.sqrt(sum / buf.length) * 3.2);
        if (lv > c.peak) c.peak = lv;
        setLevel(lv);
        c.raf = requestAnimationFrame(tick);
      };
      tick();
    }

    const mime = pickTestMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
    c.rec = rec;
    const started = Date.now();
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) c.chunks.push(e.data);
    };
    rec.onstop = () => {
      const seconds = (Date.now() - started) / 1000;
      const blob = new Blob(c.chunks, { type: mime || "audio/webm" });
      const url = URL.createObjectURL(blob);
      c.url = url;
      setResult({ url, size: blob.size, seconds, peak: c.peak });
      setPhase("done");
    };
    rec.start();
    setPhase("recording");
    c.timer = window.setTimeout(teardown, TEST_MS);
  }, [deviceId, refreshDevices, teardown]);

  const labelsKnown = devices.some((d) => d.label);
  const silent = result !== null && (result.peak < SILENCE_PEAK || result.size < MIN_BYTES);

  const barTrack: CSSProperties = {
    height: 10,
    borderRadius: 6,
    background: "var(--surface2)",
    overflow: "hidden",
    border: "1px solid var(--border)",
  };
  const barFill: CSSProperties = {
    height: "100%",
    width: `${Math.round(level * 100)}%`,
    background: level > SILENCE_PEAK ? "var(--accent)" : "var(--subtext)",
    transition: "width .06s linear",
  };
  const testBtn: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    background: "var(--accent)",
    color: "var(--onAccent)",
    fontWeight: 600,
    cursor: phase === "recording" ? "default" : "pointer",
    opacity: phase === "recording" ? 0.7 : 1,
  };

  return (
    <div className="set-row set-row-col">
      <span className="set-label">Micro</span>
      <select
        className="textinput"
        style={{ margin: 0 }}
        value={deviceId}
        onChange={(e) => onDeviceChange(e.target.value)}
      >
        <option value="">Micro par défaut du système</option>
        {devices.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId}>
            {d.label || `Micro ${i + 1}`}
          </option>
        ))}
      </select>
      {!labelsKnown && (
        <span className="set-desc">Lancez un test pour autoriser l'accès et afficher les noms des micros.</span>
      )}

      <div style={{ marginTop: 12, ...barTrack }}>
        <div style={barFill} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <button style={testBtn} onClick={runTest} disabled={phase === "recording"}>
          <Icons.Mic size={18} />
          {phase === "recording" ? "Parlez… (4 s)" : phase === "done" ? "Refaire un test" : "Tester le micro"}
        </button>
        {phase === "recording" && <span className="set-desc">Enregistrement en cours, parlez normalement.</span>}
      </div>

      {error && (
        <div className="set-desc" style={{ marginTop: 10, color: "#d9534f" }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12 }}>
          {silent ? (
            <div style={{ color: "#d9534f", fontWeight: 600 }}>
              Aucun son détecté. Vérifiez que le bon micro est sélectionné, qu'il n'est pas coupé, et que
              le navigateur a l'autorisation d'y accéder.
            </div>
          ) : (
            <div style={{ color: "var(--accent)", fontWeight: 600 }}>Micro OK — du son a bien été capté.</div>
          )}
          <div className="set-desc" style={{ marginTop: 4 }}>
            Durée {result.seconds.toFixed(1)} s · {(result.size / 1024).toFixed(1)} Ko · niveau crête{" "}
            {Math.round(result.peak * 100)} %
          </div>
          {result.size > 0 && (
            <audio controls src={result.url} style={{ width: "100%", marginTop: 10 }}>
              <track kind="captions" />
            </audio>
          )}
        </div>
      )}
    </div>
  );
}
